import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Baby, Waves, GraduationCap, ArrowRight } from "lucide-react";

const programs = [
  {
    icon: <Baby size={26} />,
    title: "Baby swimming",
    age: "8 weeks – 18 months",
    desc: "Gentle, adult-accompanied lessons — you're in the water with your little one throughout — building bonding, comfort and early water confidence.",
    img: `${process.env.PUBLIC_URL}/program-baby-swimming.jpg`,
    accent: "#0EA5E9",
  },
  {
    icon: <Waves size={26} />,
    title: "Tiddler & pre-school",
    age: "18 months – 4½ years",
    desc: "Playful, adult-accompanied lessons — you're in the water together — developing confidence, coordination and independence, with plenty of smiles.",
    img: `${process.env.PUBLIC_URL}/program-tiddler.jpg`,
    accent: "#FF7A1A",
  },
  {
    icon: <GraduationCap size={26} />,
    title: "Children's lessons",
    age: "4 years +",
    desc: "Independent lessons with structured skill development, water safety and clear progression through our STA Swim Star stages.",
    img: `${process.env.PUBLIC_URL}/program-children.jpg`,
    accent: "#0A2540",
  },
];

export default function Programs() {
  return (
    <section id="programs" className="py-24 md:py-32" data-testid="programs-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-16">
          <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Our Programmes</span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight">
            Swimming lessons for every stage
          </h2>
          <p className="mt-5 text-lg text-[#334155]">
            Whether your child is taking their first splash or building confidence and technique, we have a lesson designed to support them.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 md:gap-8">
          {/* Feature card */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="group lg:col-span-3 relative rounded-[2rem] overflow-hidden border border-slate-100 shadow-sm min-h-[440px] flex"
            data-testid="program-card-0"
          >
            <img
              src={programs[0].img}
              alt={programs[0].title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/50 to-transparent" />
            <div className="relative mt-auto p-8 md:p-10 text-white">
              <span className="grid place-items-center w-12 h-12 rounded-2xl mb-4 text-white" style={{ backgroundColor: programs[0].accent }}>
                {programs[0].icon}
              </span>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">{programs[0].title}</h3>
                <span className="text-sm font-semibold text-[#93C5FD]">{programs[0].age}</span>
              </div>
              <p className="mt-3 text-[#E2E8F0] leading-relaxed max-w-md">{programs[0].desc}</p>
            </div>
          </motion.div>

          {/* Two compact cards */}
          <div className="lg:col-span-2 flex flex-col gap-6 md:gap-8">
            {programs.slice(1).map((p, idx) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: 0.1 + idx * 0.1 }}
                className="group flex-1 bg-white rounded-[2rem] p-4 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-5"
                data-testid={`program-card-${idx + 1}`}
              >
                <div className="rounded-[1.25rem] overflow-hidden w-28 h-28 md:w-32 md:h-32 shrink-0">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="pr-2">
                  <span className="grid place-items-center w-10 h-10 rounded-xl mb-3 text-white" style={{ backgroundColor: p.accent }}>
                    {p.icon}
                  </span>
                  <h3 className="font-heading text-lg font-semibold tracking-tight text-[#0F172A] leading-snug">{p.title}</h3>
                  <span className="text-sm font-semibold text-[#64748B]">{p.age}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/classes"
            className="inline-flex items-center gap-2 bg-[#0A2540] text-white font-bold px-8 py-4 rounded-full hover:bg-[#0EA5E9] transition-all duration-300"
            data-testid="programs-explore-cta"
          >
            Explore our lessons <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}
